import { isWeb } from "@helpers/platform";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { MixedItemTag } from "@memorang/types";
import { Box, Loader, ScrollContainer, useDevice } from "@memorang/ui";
import { Redirect, useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SegmentedButtons } from "react-native-paper";
import {
	type ModuleViewMode,
	ModuleViewModeToggle,
} from "../components/ModuleViewModeToggle";
import { ModulesList } from "../components/ModulesList";
import { PurchaseDialog } from "../components/PurchaseDialog";
import { useMasteryKeyTopicsSectionData } from "../hooks/useMasteryKeyTopicsSectionData";
import { useProethicaModuleList } from "../hooks/useProethicaModuleList";

type ModuleStatusFilter = "all" | "ACTIVE" | "NOT_STARTED" | "COMPLETE";
const MODULES_CONTENT_MAX_WIDTH = 1120;

const ModulesContainer = () => {
	const router = useRouter();
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const { data: mixedItemDist, isLoading } = useMixedItemDist();
	const { getModuleListItemProps, startModule, resolveModuleItems } =
		useProethicaModuleList();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
	const [activeFilter, setActiveFilter] = useState<ModuleStatusFilter>("all");
	const [viewMode, setViewMode] = useState<ModuleViewMode>("list");
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();

	const masteryTopicsSection = useMasteryKeyTopicsSectionData(
		mixedItemDist,
		true,
	);

	if (demoExamId !== "proethica") {
		return <Redirect href="/home" />;
	}

	if (isLoading || !mixedItemDist) {
		return <Loader />;
	}

	const items = resolveModuleItems(
		(masteryTopicsSection?.data ?? []) as MixedItemTag[],
	);

	const handleShowPurchase = () => {
		if (isWeb) {
			setShowPurchaseDialog(true);
			return;
		}
		router.push({
			pathname: "/purchase",
			params: {
				productType: "STUDY_PACK",
			},
		});
	};

	const filteredItems = items.filter((tag) => {
		if (activeFilter === "all") {
			return true;
		}
		return getModuleListItemProps(tag, t).status === activeFilter;
	});

	const segmentedButtonStyle = {
		borderRadius: theme.roundness,
	};
	const segmentedLabelStyle = {
		fontFamily: theme.fonts.labelLarge.fontFamily,
		fontSize: 12,
	};

	return (
		<>
			<ScrollContainer
				style={{
					backgroundColor: theme.colors.background,
				}}
				contentContainerStyle={{
					paddingTop: isMobile ? 16 : 32,
					paddingBottom: 48,
				}}
			>
				<Box
					width="100%"
					maxWidth={MODULES_CONTENT_MAX_WIDTH}
					alignSelf="center"
					paddingHorizontal={isMobile ? 16 : 32}
					style={{ gap: 16 }}
				>
					<Box
						flexDirection={isMobile ? "column" : "row"}
						justifyContent="space-between"
						alignItems={isMobile ? "stretch" : "center"}
						width="100%"
						style={{ gap: 16 }}
					>
						<Box width={isMobile ? "100%" : 560} maxWidth="100%" minWidth={0}>
							<SegmentedButtons<ModuleStatusFilter>
								value={activeFilter}
								onValueChange={setActiveFilter}
								density="small"
								style={{
									width: "100%",
									borderRadius: theme.roundness,
								}}
								theme={{ roundness: theme.roundness }}
								buttons={[
									{
										value: "all",
										label: "All",
										icon: "view-grid-plus-outline",
										style: segmentedButtonStyle,
										labelStyle: segmentedLabelStyle,
										showSelectedCheck: false,
									},
									{
										value: "ACTIVE",
										label: "In progress",
										icon: "progress-clock",
										style: segmentedButtonStyle,
										labelStyle: segmentedLabelStyle,
										showSelectedCheck: false,
									},
									{
										value: "NOT_STARTED",
										label: "Not started",
										icon: "play-circle-outline",
										style: segmentedButtonStyle,
										labelStyle: segmentedLabelStyle,
										showSelectedCheck: false,
									},
									{
										value: "COMPLETE",
										label: "Completed",
										icon: "check-circle-outline",
										style: segmentedButtonStyle,
										labelStyle: segmentedLabelStyle,
										showSelectedCheck: false,
									},
								]}
							/>
						</Box>
						<Box alignSelf={isMobile ? "flex-start" : "center"}>
							<ModuleViewModeToggle
								value={viewMode}
								onValueChange={setViewMode}
							/>
						</Box>
					</Box>
					<ModulesList
						items={filteredItems}
						loading={masteryTopicsSection?.loading}
						handleSelectTopic={startModule}
						handleShowPurchase={handleShowPurchase}
						getModuleListItemProps={getModuleListItemProps}
						viewMode={viewMode}
					/>
				</Box>
			</ScrollContainer>
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setShowPurchaseDialog(false)}
					productType="STUDY_PACK"
				/>
			)}
		</>
	);
};

export default ModulesContainer;
